import { useState, useEffect } from "react";
import cx from "classnames";
import Chart from "react-apexcharts";
import SiteWrapper from "../../SiteWrapper";
import ContentGutter from "../../ContentGutter";
import t, { AsHtml } from "../../../utils/translate";
import { getWeightLocalized } from "../../../utils/weightUnits";
import { getWeightLossChartData } from "components/Profile/utils/getWeightLossResults";
import useAppContext from "components/AppContext";

import styles from "./styles.module.scss";

export default function WeightLossChart({
  profile,
  newQuiz,
  showHeader = true,
  showDisclaimer = true,
  goalMetKey = "WEIGHT_LOSS_CHART_GOAL_MET_V2",
  goalNotMetKey = "WEIGHT_LOSS_CHART_GOAL_NOT_MET_V2"
}) {
  const { assessment, answers } = useAppContext();
  const [chartHeight, setChartHeight] = useState("auto");
  useEffect(() => {
    if (newQuiz.isSequence) {
      return;
    }

    onScreenResize();

    window.addEventListener("resize", onScreenResize);

    return () => window.removeEventListener("resize", onScreenResize);
  }, [chartHeight, setChartHeight, JSON.stringify(newQuiz)]);

  const onScreenResize = () => {
    if (chartHeight === "350px" && window.innerWidth < 991) {
      setChartHeight("auto");
    } else if (chartHeight === "auto" && window.innerWidth >= 991) {
      setChartHeight("350px");
    }
  };

  if (!profile?.imperial?.desiredWeight) {
    return null;
  }

  const metric = profile.measurementSystem === "metric";
  const {
    predictedWeight,
    seriesData,
    categories,
    graphUnit,
    weightUnit,
    maxLoss,
    desiredWeightLossPounds,
    estimatedWeightChange,
    estimatedDate,
    alternateWeightChange,
    alternateMonths
  } = getWeightLossChartData({
    profile,
    answers: newQuiz.isUni5 ? answers : [],
    questions: newQuiz.isUni5 ? assessment?.questions : []
  });

  let ymin = Math.floor(predictedWeight / 5) * 5;

  const ymax = metric
    ? Math.ceil(profile.metric.weight / 5) * 5
    : Math.ceil(profile.imperial.weight / 5) * 5;

  const every5 = Math.round((ymax - ymin) / 5);
  const every10 = Math.round((ymax - ymin) / 10);
  let ticks = every5 > 5 ? every10 : every5;

  // Increase starting value of Y axis so the last marker is not cut off.
  if (ymin >= Math.round(predictedWeight)) {
    ymin = ticks === every5 ? ymin - 5 : ymin - 10;
    ticks++;
  }

  const lineColor = "#5871E5";
  const marker = {
    size: 6,
    fillColor: "#47CAC2",
    strokeColor: "#FFFFFF",
    seriesIndex: 1
  };

  const options = {
    fill: {
      colors: "#BFE3E2",
      type: "gradient",
      gradient: {
        opacityFrom: 1,
        opacityTo: 1,
        type: "vertical",
        shadeIntensity: 0,
        colorStops: [
          [
            {
              offset: 0,
              opacity: 0.7,
              color: "#BFE3E2"
            },
            {
              offset: 50,
              opacity: 0.7,
              color: "#DCF0F2"
            },
            {
              offset: 100,
              opacity: 0.7,
              color: "#EFF4F7"
            }
          ],
          [
            {
              offset: 0,
              opacity: 1,
              color: "#5670DC"
            },
            {
              offset: 50,
              opacity: 1,
              color: "#67A2DC"
            },
            {
              offset: 100,
              opacity: 1,
              color: "#73CDCB"
            }
          ]
        ]
      }
    },
    grid: {
      borderColor: "#3C34E533"
    },
    chart: {
      selection: {
        enabled: false
      },
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: false }
    },
    stroke: {
      curve: "smooth",
      colors: [lineColor, lineColor],
      width: [0, 4]
    },
    legend: {
      show: false
    },
    tooltip: {
      enabled: false
    },
    markers: {
      discrete: [
        {
          dataPointIndex: 0,
          ...marker
        },
        {
          dataPointIndex: seriesData.length - 1,
          ...marker
        }
      ]
    },
    xaxis: {
      axisBorder: {
        show: false
      },
      categories,
      labels: {
        rotate: 0,
        style: {
          fontFamily: "Gellix",
          fontSize: "12px",
          fontWeight: 350,
          colors: "#454545"
        }
      },
      axisTicks: {
        show: false
      }
    },
    yaxis: {
      labels: {
        formatter: weight => {
          return Math.round(weight) + ` ${graphUnit}`;
        },
        style: {
          fontFamily: "Gellix",
          fontSize: "12px",
          fontWeight: 350,
          colors: "#454545"
        }
      }
    }
  };

  // Default Y axis settings appear to work best for stones.
  if (weightUnit !== "stones") {
    options.yaxis = {
      ...options.yaxis,
      min: ymin,
      max: ymax,
      tickAmount: ticks
    };
  } else if (Math.abs(maxLoss) < 1) {
    options.yaxis = {
      ...options.yaxis,
      min: predictedWeight - 1,
      max: predictedWeight + Math.abs(maxLoss) + 1,
      tickAmount: 2
    };
  }

  const series = [
    {
      type: "area",
      data: seriesData
    },
    {
      type: "line",
      data: [...seriesData]
    }
  ];

  const statement =
    estimatedWeightChange < desiredWeightLossPounds
      ? t(goalNotMetKey, {
          alternateWeightChange,
          alternateMonths,
          name: profile.firstName
        })
      : t(goalMetKey, {
          desiredWeight: getWeightLocalized(
            profile,
            profile[profile.measurementSystem].desiredWeight
          ).numberFormatted,
          estimateDate: estimatedDate,
          name: profile.firstName
        });

  return (
    <div
      className={cx({
        [styles.wrapperEli]: newQuiz.eli,
        [styles.wrapper]: !newQuiz.isUni,
        [styles.wrapperUni]: newQuiz.isUni,
        [styles.noHeader]: !showHeader
      })}
    >
      <SiteWrapper size="small">
        <ContentGutter>
          {showHeader && (
            <h1 data-testid="chart-header" className={styles.header}>
              <AsHtml value={statement} />
            </h1>
          )}
          <div
            className={cx(styles.chart, {
              [styles.isTest]: window?.ww?.isTest
            })}
            aria-hidden="true"
          >
            <Chart options={options} series={series} height={chartHeight} />
          </div>
          {showDisclaimer && (
            <p className={styles.disclaimer}>{t("WEIGHT_LOSS_DISCLAIMER")}</p>
          )}
        </ContentGutter>
      </SiteWrapper>
    </div>
  );
}
